/*
   Data layer for evaluation runs: /api/v1/eval-runs on the backend (routes_eval_runs.py).
   Runs are an admin-only development tool: they are never shown to clients, and an admin moves
   each one through Draft → Reviewed → Archived.
*/

import {
  queryOptions,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query"

import { parseDetectorProfileResolved } from "@/components/domain/detector-profile"
import { http } from "@/lib/http"
import { qk } from "@/lib/query-keys"

import type {
  EvalFinding,
  EvalLabel,
  EvalLabelRow,
  EvalMark,
  EvalMasterMapping,
  EvalRun,
  EvalRunId,
  EvalRunStatus,
  FindingOutput,
  MarkHow,
  MarkOutcome,
  MarkScope,
  MasterMappingKind,
  MasterSource,
  PointBox,
  Remark,
  RemarkTargetKind,
  ScopeSource,
} from "../eval-runs-data"
import { parseDatasetProfile } from "../dataset-profile"

// ── Wire types (mirror schemas/eval_runs.py) ───────────────────────────────

type BoxWire = [number, number, number, number]

export interface EvalRunWire {
  id: number
  name: string
  dataset: string
  method: string
  notes: string | null
  summary: Record<string, number | null>
  service_revision: string | null
  client_id: number | null
  status: EvalRunStatus
  created_at: string
  label_count: number
  /** What the run's reviews resolved their detector settings to. Narrowed; absent before the feature. */
  detector_profile?: unknown
  /** The derived profile file's settings and evidence, when the run used one. */
  dataset_profile?: unknown
  /** Absent from servers that predate mark scope tags. */
  mark_scope_method?: string | null
  summary_recomputed?: string[] | null
  scope_decisions?: number | null
}

interface CountsWire {
  caught: number
  escalated: number
  missed: number
  not_checked: number
  out_of_rules: number
  flags: number
  handoffs: number
}

interface MasterFixWire {
  what: string
  why?: string
  evidence?: string[]
  serious?: boolean
  date?: string | null
}

export interface MasterMappingWire {
  kind: MasterMappingKind
  source: MasterSource
  rule?: string
  master_file: string
  master_date?: string | null
  supplements?: string[]
  reg_nr?: string | null
  reg_source?: string | null
  fixes?: MasterFixWire[]
}

export interface EvalLabelWire {
  mid: string
  product: string
  kind: string
  file_name: string
  has_pdf: boolean
  /** Absent from servers that predate the master PDF. */
  has_master_pdf?: boolean
  /** Absent from servers that predate the master mapping, null when the run did not record it. */
  master?: MasterMappingWire | null
  counts: CountsWire
  comment_count: number
}

interface MarkWire {
  id: string
  page: number
  rect: BoxWire | null
  comment: string
  highlighted: string
  rules: string[]
  outcome: MarkOutcome
  how: MarkHow | null
  finding_id: number | null
  rejected_by_tiebreak: boolean
  tiebreak_reason: string | null
  /** Absent on runs published before marks were tagged. */
  scope?: string
  scope_reason?: string | null
  scope_source?: string | null
  ai_scope?: string | null
  ai_scope_reason?: string | null
  scope_set_by_name?: string | null
  scope_set_at?: string | null
}

interface FindingWire {
  id: number
  rule_id: string
  output: FindingOutput
  message: string
  divergence: string | null
  page: number | null
  boxes: { page: number; bbox: BoxWire }[]
}

/** Label page → master page, 0-based; JSON object keys arrive as strings. */
type PageMapWire = Record<string, unknown>

export interface EvalLabelDetailWire extends EvalLabelWire {
  run_id: number
  payload: { marks: MarkWire[]; findings: FindingWire[]; page_map?: PageMapWire | null }
  /** Accepted beside the payload as well; absent on runs published before it. */
  page_map?: PageMapWire | null
}

export interface RemarkWire {
  id: number
  target_kind: RemarkTargetKind
  target_ref: string
  author_role: string
  author_name: string | null
  body: string
  created_at: string
}

// ── Adapters ─────────────────────────────────────────────────────────────

const box = (b: BoxWire): PointBox => [b[0], b[1], b[2], b[3]]

const SCOPES: readonly string[] = ["in_scope", "master_text", "outside_documents", "advisory"]
const isScope = (s: string): s is MarkScope => SCOPES.includes(s)

/** A missing tag is in scope, and so is one this console does not know: when in doubt, count it. */
export function toScope(s: string | null | undefined): MarkScope {
  return s != null && isScope(s) ? s : "in_scope"
}

/** An unrecognised or missing source is "default": nothing is claimed about who decided. */
function toSource(s: string | null | undefined): ScopeSource {
  return s === "ai" || s === "admin" ? s : "default"
}

/**
 * The summary as the console reads it. An admin's scope decision is a tag too: a run published
 * without tags (`mark_scope_loaded` 0) whose in-scope figures the server recomputed after one has
 * in-scope figures to show.
 */
function toSummary(w: EvalRunWire): EvalRun["summary"] {
  const rescored = (w.summary_recomputed ?? []).some((k) => k.startsWith("in_scope_"))
  return rescored && w.summary["mark_scope_loaded"] === 0
    ? { ...w.summary, mark_scope_loaded: 1 }
    : w.summary
}

export function toEvalRun(w: EvalRunWire): EvalRun {
  return {
    id: w.id as EvalRunId,
    name: w.name,
    dataset: w.dataset,
    method: w.method,
    notes: w.notes,
    summary: toSummary(w),
    serviceRevision: w.service_revision,
    status: w.status,
    createdAt: w.created_at,
    labelCount: w.label_count,
    detectorProfile: parseDetectorProfileResolved(w.detector_profile),
    datasetProfile: parseDatasetProfile(w.dataset_profile),
    markScopeMethod: w.mark_scope_method ?? null,
    summaryRecomputed: w.summary_recomputed ?? [],
    scopeDecisions: w.scope_decisions ?? null,
  }
}

export function toMaster(w: MasterMappingWire): EvalMasterMapping {
  return {
    kind: w.kind,
    source: w.source,
    rule: w.rule ?? "",
    masterFile: w.master_file,
    masterDate: w.master_date ?? null,
    supplements: w.supplements ?? [],
    regNr: w.reg_nr ?? null,
    regSource: w.reg_source ?? null,
    fixes: (w.fixes ?? []).map((f) => ({
      what: f.what,
      why: f.why ?? "",
      evidence: f.evidence ?? [],
      serious: f.serious ?? false,
      date: f.date ?? null,
    })),
  }
}

export function toLabelRow(w: EvalLabelWire): EvalLabelRow {
  return {
    mid: w.mid,
    product: w.product,
    kind: w.kind,
    fileName: w.file_name,
    hasPdf: w.has_pdf,
    hasMasterPdf: w.has_master_pdf ?? false,
    master: w.master ? toMaster(w.master) : null,
    counts: {
      caught: w.counts.caught,
      escalated: w.counts.escalated,
      missed: w.counts.missed,
      notChecked: w.counts.not_checked,
      flags: w.counts.flags,
      handoffs: w.counts.handoffs,
    },
    remarkCount: w.comment_count,
  }
}

function toMark(w: MarkWire): EvalMark {
  return {
    id: w.id,
    page: w.page,
    rect: w.rect ? box(w.rect) : null,
    comment: w.comment,
    highlighted: w.highlighted,
    rules: w.rules,
    outcome: w.outcome,
    how: w.how,
    findingId: w.finding_id,
    rejectedByTiebreak: w.rejected_by_tiebreak,
    tiebreakReason: w.tiebreak_reason,
    scope: toScope(w.scope),
    scopeReason: w.scope_reason ?? null,
    scopeSource: toSource(w.scope_source),
    aiScope: w.ai_scope != null && isScope(w.ai_scope) ? w.ai_scope : null,
    aiScopeReason: w.ai_scope_reason ?? null,
    scopeSetByName: w.scope_set_by_name ?? null,
    scopeSetAt: w.scope_set_at ?? null,
  }
}

function toFinding(w: FindingWire): EvalFinding {
  return {
    id: w.id,
    ruleId: w.rule_id,
    output: w.output,
    message: w.message,
    divergence: w.divergence,
    page: w.page,
    boxes: w.boxes.map((b) => ({ page: b.page, bbox: box(b.bbox) })),
  }
}

const isPage = (n: unknown): n is number => typeof n === "number" && Number.isInteger(n) && n >= 0

/** The page map, keeping only entries that name two pages: a malformed one is no entry at all. */
export function toPageMap(w: PageMapWire | null | undefined): ReadonlyMap<number, number> {
  const map = new Map<number, number>()
  for (const [key, value] of Object.entries(w ?? {})) {
    const page = key.trim() === "" ? Number.NaN : Number(key)
    if (isPage(page) && isPage(value)) map.set(page, value)
  }
  return map
}

export function toLabel(w: EvalLabelDetailWire): EvalLabel {
  return {
    ...toLabelRow(w),
    marks: w.payload.marks.map(toMark),
    findings: w.payload.findings.map(toFinding),
    pageMap: toPageMap(w.payload.page_map ?? w.page_map),
  }
}

export function toRemark(w: RemarkWire): Remark {
  return {
    id: w.id,
    targetKind: w.target_kind,
    targetRef: w.target_ref,
    authorRole: w.author_role,
    authorName: w.author_name,
    body: w.body,
    createdAt: w.created_at,
  }
}

// ── Queries ──────────────────────────────────────────────────────────────

/** The list routes' own maximum page. */
const PAGE = 1000

/**
 * Every row of a `limit`/`offset` list route. One request covers today's runs, labels and remarks;
 * past a full page it keeps reading, so a large run is not cut off at the page size.
 */
async function getAll<T>(path: string, signal: AbortSignal): Promise<T[]> {
  const rows: T[] = []
  for (let offset = 0; ; offset += PAGE) {
    const page = await http.get<T[]>(path, { params: { limit: PAGE, offset }, signal })
    rows.push(...page)
    if (page.length < PAGE) return rows
  }
}
const labelPath = (runId: number, mid: string) =>
  `/api/v1/eval-runs/${String(runId)}/labels/${encodeURIComponent(mid)}`

export const evalRunsQuery = () =>
  queryOptions({
    queryKey: qk.evalRuns.list(),
    queryFn: async ({ signal }) =>
      (await getAll<EvalRunWire>("/api/v1/eval-runs", signal)).map(toEvalRun),
  })

export const evalRunQuery = (runId: number) =>
  queryOptions({
    queryKey: qk.evalRuns.detail(runId),
    queryFn: async ({ signal }) =>
      toEvalRun(await http.get<EvalRunWire>(`/api/v1/eval-runs/${String(runId)}`, { signal })),
  })

export const evalLabelsQuery = (runId: number) =>
  queryOptions({
    queryKey: qk.evalRuns.labels(runId),
    queryFn: async ({ signal }) =>
      (await getAll<EvalLabelWire>(`/api/v1/eval-runs/${String(runId)}/labels`, signal)).map(
        toLabelRow,
      ),
  })

export const evalLabelQuery = (runId: number, mid: string) =>
  queryOptions({
    queryKey: qk.evalRuns.label(runId, mid),
    queryFn: async ({ signal }) =>
      toLabel(await http.get<EvalLabelDetailWire>(labelPath(runId, mid), { signal })),
  })

export const evalRemarksQuery = (runId: number, mid: string) =>
  queryOptions({
    queryKey: qk.evalRuns.remarks(runId, mid),
    queryFn: async ({ signal }) =>
      (await getAll<RemarkWire>(`${labelPath(runId, mid)}/comments`, signal)).map(toRemark),
  })

/** The label PDF as bytes for pdf.js, through the authenticated transport. */
export function fetchLabelPdf(
  runId: number,
  mid: string,
  signal?: AbortSignal,
): Promise<Uint8Array> {
  return http.bytes(`${labelPath(runId, mid)}/file`, signal ? { signal } : undefined)
}

/** The label's master PDF as bytes for pdf.js; the server answers 404 when the run has none. */
export function fetchMasterPdf(
  runId: number,
  mid: string,
  signal?: AbortSignal,
): Promise<Uint8Array> {
  return http.bytes(`${labelPath(runId, mid)}/master-file`, signal ? { signal } : undefined)
}

// ── Mutations ────────────────────────────────────────────────────────────

export interface RemarkInput {
  targetKind: RemarkTargetKind
  targetRef: string
  body: string
}

export function usePostRemarkMutation(
  runId: number,
  mid: string,
): UseMutationResult<Remark, Error, RemarkInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: RemarkInput) =>
      toRemark(
        await http.post<RemarkWire>(`${labelPath(runId, mid)}/comments`, {
          target_kind: input.targetKind,
          target_ref: input.targetRef,
          body: input.body,
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.evalRuns.remarks(runId, mid) })
      void queryClient.invalidateQueries({ queryKey: qk.evalRuns.labels(runId) })
    },
  })
}

export interface RunEdit {
  name?: string
  notes?: string
  status?: EvalRunStatus
}

export function useUpdateRunMutation(runId: number): UseMutationResult<EvalRun, Error, RunEdit> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (edit: RunEdit) =>
      toEvalRun(await http.patch<EvalRunWire>(`/api/v1/eval-runs/${String(runId)}`, edit)),
    // The answer is the run as saved: write it straight into the cache, so a control bound to it
    // (the status select) never reads the old value between the save and the refetch. Returning
    // the invalidation keeps the mutation pending until the list has caught up too.
    onSuccess: (saved) => {
      queryClient.setQueryData(qk.evalRuns.detail(runId), saved)
      return queryClient.invalidateQueries({ queryKey: qk.evalRuns.all })
    },
  })
}

export function useDeleteRunMutation(runId: number): UseMutationResult<void, Error, void> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => http.delete<undefined>(`/api/v1/eval-runs/${String(runId)}`),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: qk.evalRuns.detail(runId) })
      void queryClient.invalidateQueries({ queryKey: qk.evalRuns.list() })
    },
  })
}

export interface ScopeDecision {
  scope: MarkScope
  reason: string | null
}

export interface ScopeDecisionWire {
  dataset: string
  mark_id: string
  scope: string
  reason: string | null
  set_by_name: string | null
  updated_at: string
}

const scopePath = (runId: number, markId: string) =>
  `/api/v1/eval-runs/${String(runId)}/marks/${encodeURIComponent(markId)}/scope`

/**
 * An admin's scope decision on one mark (PUT), or its withdrawal back to the classifier's call
 * (DELETE). A decision moves the run's figures, which the server recomputes, and it is stored per
 * dataset, so every run is refetched: the list's figures, each run's summary and its labels.
 */
export function useSetMarkScopeMutation(
  runId: number,
  markId: string,
): UseMutationResult<ScopeDecisionWire | undefined, Error, ScopeDecision | null> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (decision: ScopeDecision | null) =>
      decision
        ? http.put<ScopeDecisionWire>(scopePath(runId, markId), decision)
        : http.delete<undefined>(scopePath(runId, markId)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.evalRuns.all }),
  })
}
